// Next O High School Performance System — periodized curriculum (from official PDF).
export const TRAINING_PLAN = {
  inSeason: {
    label: "In-Season",
    goal: "Maintain explosive power & strength, optimize recovery between 2–3 weekly matches, sharpen game-speed technical touch, and prevent soft-tissue injuries.",
    seasons: { boys: "August – November (Fall)", girls: "March – June (Spring)" },
    microCycle: [
      { day: "Mon", focus: "Match Day 1 / Hard Practice" },
      { day: "Tue", focus: "Active Recovery + Strength" },
      { day: "Wed", focus: "Match Day 2 / Mid-Week Match" },
      { day: "Thu", focus: "Tactical + Next O Touch Activation" },
      { day: "Fri", focus: "Match Day 3 / Pre-Game Prep" },
    ],
    routines: [
      {
        id: "is_strength",
        title: "In-Season Strength & Injury Prevention",
        when: "Post-Practice / Non-Match Days · 20–30 min · 2×/week",
        note: "Done in 2 supersets to minimize time and avoid central nervous system fatigue.",
        blocks: [
          {
            name: "Superset A — 2 Rounds",
            exercises: [
              { name: "Goblet Squats or Trap Bar Deadlifts", sets: 3, detail: "3 × 5 reps @ 80% 1RM — heavy, low volume for power preservation" },
              { name: "Single-Leg Romanian Deadlifts (RDLs)", sets: 3, detail: "3 × 6 reps/leg — hamstring / ACL injury prevention" },
            ],
          },
          {
            name: "Superset B — 2 Rounds",
            exercises: [
              { name: "Nordic Hamstring Curls (eccentric focus)", sets: 3, detail: "3 × 4 reps — high-speed sprint durability" },
              { name: "Copenhagen Side Planks", sets: 3, detail: "3 × 20-sec holds/side — adductor & groin strain prevention" },
            ],
          },
        ],
      },
      {
        id: "is_touch",
        title: "In-Season Touch & Speed Activation",
        when: "Pre-Practice / Match · 15 min",
        blocks: [
          {
            name: "Activation Circuit",
            exercises: [
              { name: "High-Frequency Wall Bounces", sets: 3, detail: "3 × 30 sec — 1-touch, alternating feet, 3 ft from wall" },
              { name: "Deceleration & Plant-and-Cut Mechanics", sets: 4, detail: "4 × 5-yd sprint → 1-step rapid decel → 90° explosion out" },
              { name: "Scanning Activation (Observation)", sets: 1, detail: "2-touch passing while calling out numbers flashed by a partner behind" },
            ],
          },
        ],
      },
    ],
  },
  offSeason: {
    label: "Off-Season",
    goal: "Hypertrophy, maximal strength, rate of force development (RFD), explosive multi-directional speed, and master-level ball manipulation.",
    seasons: { boys: "December – July (Winter/Spring/Summer)", girls: "July – February (Summer/Fall/Winter)" },
    split: [
      {
        id: "os_day1", day: "Day 1", title: "Lower Body Maximal Strength & First-Step Acceleration",
        parts: [
          {
            name: "Part A — Strength & Power (Gym)",
            exercises: [
              { name: "Trap Bar Deadlifts / Barbell Back Squats", sets: 4, detail: "4 × 5 @ 85% 1RM" },
              { name: "Bulgarian Split Squats", sets: 3, detail: "3 × 8/leg — unilateral leg strength" },
              { name: "Barbell Hip Thrusts", sets: 3, detail: "3 × 10 — glute activation for acceleration" },
              { name: "Calf & Tibialis Raises", sets: 3, detail: "3 × 15 — ankle stiffness & shin-splint prevention" },
            ],
          },
          {
            name: "Part B — Acceleration & Plyometrics (Turf/Field)",
            exercises: [
              { name: "Prowler/Sled or Band-Resisted Sprints", sets: 5, detail: "5 × 10 yd — full 90-sec rest between reps" },
              { name: "Depth Jumps to Broad Jump", sets: 4, detail: "4 × 3 — explosive drop off a 12-inch box" },
            ],
          },
        ],
      },
      {
        id: "os_day2", day: "Day 2", title: "Advanced Ball Mastery & Spatial Intelligence (Observation)",
        parts: [
          {
            name: "Part A — The Next O Technical Matrix (20 min)",
            exercises: [
              { name: 'The "O" Chop-to-L-Cut Combo', sets: 4, detail: "4 × 10 — high-speed direction change in a tight 3×3 ft grid" },
              { name: "Sole-Roll Step-Over Cut", sets: 4, detail: "4 × 8/leg — simulating beating a wing-back 1v1" },
              { name: "Air Cushion & First-Touch Drops", sets: 5, detail: "5 × 4 — cushion a 20-ft throw, aggressive first touch before 2nd bounce" },
            ],
          },
          {
            name: "Part B — Wall-Rebound & Scanning (15 min)",
            exercises: [
              { name: "Double-Check Wall Passes", sets: 1, detail: "Firm pass off wall → look over left shoulder → right shoulder → receive on back foot → explode sideways" },
            ],
          },
        ],
      },
      {
        id: "os_day3", day: "Day 3", title: "Upper Body Hypertrophy, Core & Deceleration / Agility",
        parts: [
          {
            name: "Part A — Upper Body & Core Stability (Gym)",
            exercises: [
              { name: "Incline Dumbbell Press", sets: 4, detail: "4 × 8 — size to hold off defenders on shielding plays" },
              { name: "Weighted Pull-Ups / Lat Pulldowns", sets: 4, detail: "4 × 8 — back strength for duel posture" },
              { name: "Pallof Press & Hanging Knee Raises", sets: 3, detail: "3 × 12 — rotational core resistance" },
            ],
          },
          {
            name: "Part B — Multi-Directional Agility & COD (Turf/Field)",
            exercises: [
              { name: "5-10-5 Pro Agility Shuttle", sets: 5, detail: "5 sets — low hips on touch points, 100% exit acceleration" },
              { name: '3-Cone "L" Drill', sets: 4, detail: "4 sets — plant outside foot hard, zero wasted motion" },
              { name: "Micro-Cone Weave to Unbalanced Strike", sets: 4, detail: "4 sets — zig-zag 5 cones (12 in apart) → burst → weak-foot strike" },
            ],
          },
        ],
      },
      {
        id: "os_day4", day: "Day 4", title: "Conditioning (RSA) & Game-Speed Finishing",
        parts: [
          {
            name: "Part A — High-Intensity Soccer-Specific Conditioning",
            exercises: [
              { name: "120-Yard Shuttle Runs", sets: 4, detail: "10/20/30 yd & back — goal < 30 sec/set · 4 sets, 2-min rest" },
              { name: "30-15 Intermittent Fitness Simulation", sets: 10, detail: "30 sec sprint / 15 sec walk × 10 rounds" },
            ],
          },
          {
            name: "Part B — Match-Speed Technical Finishing (20 min)",
            exercises: [
              { name: "Driven Long Balls & Weak-Foot Half-Volleys", sets: 4, detail: "4 × 6/leg — 30-yd diagonal pinging into target nets" },
              { name: "Box Entry & One-Touch Finishing", sets: 1, detail: "Receive a low driven cross on the run → redirect into side netting, one touch under pressure" },
            ],
          },
        ],
      },
    ],
  },
};
